import { useFlags } from "~/features/feature-flags"
import { Sliders } from "@phosphor-icons/react"

import { AdjustmentManagerDialog } from "../forecast/dialogs/AdjustmentManagerDialog"
import { Button } from "~/components/reusable/Button"
import MultiEditDialog from "./MultiEditDialog/MultiEditDialog"
import Commandbar from "./Commandbar"
import { PurchaseOrderDialog } from "~/components/purchaseOrder/PurchaseOrderDialog"
import { EditSkuTagsDialog } from "./Tags/EditSkuTagsDialog"
import { useVariantsByIds } from "~/hooks/useVariants"
import { getCommonVendors } from "~/utils/vendorUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { RemoveVariantsFromCollectionDialog } from "../collections/dialogs/RemoveVariantsFromCollectionDialog"

type MultiEditSkuPanelProps = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedVariantIds: () => void
  collectionId?: number
  hideReorder?: boolean
  hideAdjust?: boolean
  extraActions?: React.ReactNode
  /** Rendered last; replaces the inline Edit and Tags actions, which it is expected to offer. */
  renderOverflow?: (variants: IVariantSummary[]) => React.ReactNode
}

export function MultiEditSkuPanel({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedVariantIds,
  collectionId,
  hideReorder,
  hideAdjust,
  extraActions,
  renderOverflow,
}: MultiEditSkuPanelProps) {
  const { adjustments: adjustmentsFlag } = useFlags()
  const variants = useVariantsByIds(selectedVariantIds)
  const skuIds = variants.flatMap((v: IVariantSummary) => v.skuIds)
  const commonVendors = getCommonVendors(variants)

  return (
    <>
      {selectedVariantCount > 0 && (
        <Commandbar count={selectedVariantCount} onClear={clearSelectedVariantIds}>
          {!renderOverflow && (
            <MultiEditDialog
              selectedSkuIds={skuIds}
              selectedVariantCount={selectedVariantCount}
              resetSelectedProductIds={clearSelectedVariantIds}
              commonVendors={commonVendors}
            />
          )}
          {adjustmentsFlag && !hideAdjust && (
            <AdjustmentManagerDialog variantIds={selectedVariantIds} allVariants={variants}>
              <Button asChild variant="neutral" size="sm">
                <Sliders />
                Adjust
              </Button>
            </AdjustmentManagerDialog>
          )}
          {!hideReorder && <PurchaseOrderDialog variants={variants} />}
          {!renderOverflow && <EditSkuTagsDialog variantIds={selectedVariantIds} />}
          {collectionId !== undefined && (
            <RemoveVariantsFromCollectionDialog
              variantIds={selectedVariantIds}
              collectionId={collectionId}
            />
          )}
          {extraActions}
          {renderOverflow?.(variants)}
        </Commandbar>
      )}
    </>
  )
}
